import type { Asset, Clip, Project } from '../types'
import type { VaideoImport } from './api'
import { clipFromAsset, newProject, newTrack, subtitleClip } from './model'

const ASPECT_SIZES: Record<string, [number, number]> = {
  '16:9': [1920, 1080], '9:16': [1080, 1920], '1:1': [1080, 1080], '4:5': [1080, 1350],
}

/**
 * Split narration into short caption chunks (≤ maxWords / ≤ maxChars),
 * breaking preferably at punctuation. Timing is proportional to characters.
 */
export function chunkCaption(text: string, maxWords = 6, maxChars = 34): string[] {
  const words = text.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean)
  const out: string[] = []
  let cur: string[] = []
  for (const w of words) {
    const candidate = [...cur, w].join(' ')
    if (cur.length && (cur.length >= maxWords || candidate.length > maxChars)) {
      out.push(cur.join(' '))
      cur = []
    }
    cur.push(w)
    if (/[.!?;:]$/.test(w) && cur.length >= 2) {
      out.push(cur.join(' '))
      cur = []
    }
  }
  if (cur.length) {
    // avoid a dangling single word
    if (cur.length === 1 && out.length && (out[out.length - 1] + ' ' + cur[0]).length <= maxChars + 8) {
      out[out.length - 1] += ' ' + cur[0]
    } else out.push(cur.join(' '))
  }
  return out
}

export function projectFromVaideo(r: VaideoImport, uid: string): Project {
  // Canvas = the format chosen in Vaideo at the standard editing size (text and
  // subtitle sizes are in canvas pixels). The real pixel size of the MP4 goes
  // into `origin` and drives the default export resolution.
  const vw = r.video.info.width
  const vh = r.video.info.height
  const [w, h] = ASPECT_SIZES[r.aspectRatio ?? ''] ?? (vw > 0 && vh > 0 ? [vw, vh] : [1920, 1080])
  const p = newProject(w, h)
  const fps = Math.round(r.video.info.fps || 0)
  if (fps >= 10 && fps <= 60) p.settings.fps = fps
  p.name = r.sourceTitle || `Vaideo ${uid}`
  p.vaideoUid = uid
  p.origin = {
    uid,
    aspectRatio: r.aspectRatio || `${w}×${h}`,
    width: vw > 0 && vh > 0 && Math.abs(vw / vh - w / h) < 0.01 ? vw : w,
    height: vw > 0 && vh > 0 && Math.abs(vw / vh - w / h) < 0.01 ? vh : h,
    fps: p.settings.fps,
    language: r.language ?? null,
    durationSec: r.durationSec ?? r.video.info.durationSec,
  }
  const subsTrack = newTrack('video', 'Subtítulos')
  const main = p.tracks.find((t) => t.kind === 'video')!
  const audioTrack = p.tracks.find((t) => t.kind === 'audio')!
  p.tracks = [subsTrack, ...p.tracks]
  main.name = 'Vídeo'
  audioTrack.name = 'Voz'

  const video: Asset = { id: r.video.id, name: `${p.name}.mp4`, info: r.video.info, source: 'vaideo' }
  p.assets.push(video)
  const vclip = clipFromAsset(video, main.id, 0)
  p.clips.push(vclip)
  if (r.audio) {
    const audio: Asset = { id: r.audio.id, name: `${p.name} (voz).m4a`, info: r.audio.info, source: 'vaideo' }
    p.assets.push(audio)
    vclip.audio.muted = true // voice lives on its own track
    p.clips.push(clipFromAsset(audio, audioTrack.id, 0))
  }

  if (!r.burnedSubtitles) {
    const scenes = (r.scenes || []).filter((s) => s.narration && s.narration.trim() && s.endSec > s.startSec)
    const lastEnd = scenes.reduce((m, s) => Math.max(m, s.endSec), 0)
    const k = lastEnd > 0 && video.info.durationSec > 0 ? video.info.durationSec / lastEnd : 1
    const subs: Clip[] = []
    for (const s of scenes) {
      const chunks = chunkCaption(s.narration)
      const total = chunks.reduce((n, c) => n + c.length, 0) || 1
      let t = s.startSec * k
      const span = (s.endSec - s.startSec) * k
      for (const ch of chunks) {
        const d = (span * ch.length) / total
        subs.push(subtitleClip(subsTrack.id, t, Math.max(0.3, d - 0.02), ch))
        t += d
      }
    }
    p.clips.push(...subs)
  }
  return p
}
